export interface Viewer {
  username: string;
  displayName: string;
  id: string;
  type: 'viewer' | 'moderator' | 'vip' | 'broadcaster';
  profileImageUrl?: string;
  isBot?: boolean;
  joinedAt: Date;
  badges: string[];
}

export class TwitchViewersService {
  private accessToken: string;
  private clientId: string;
  private knownBots: Set<string> = new Set();
  private botsLoaded: boolean = false;
  private botsLoadingPromise: Promise<void> | null = null;

  constructor(clientId: string, accessToken: string) {
    this.clientId = clientId;
    this.accessToken = accessToken;
    // Start loading bots immediately but don't await
    this.botsLoadingPromise = this.loadKnownBots();
  }

  private async loadKnownBots(): Promise<void> {
    if (this.botsLoaded) return;

    try {
      console.log('🤖 Starting to load known bots...');
      
      // Try multiple possible paths for the bots file
      const possiblePaths = [
        '/chatbots.json',
        './chatbots.json', 
        '/public/chatbots.json',
        './public/chatbots.json',
        new URL('../public/chatbots.json', import.meta.url).href
      ];
      
      let data = null;
      
      for (const path of possiblePaths) {
        try {
          console.log(`🤖 Trying path: ${path}`);
          const response = await fetch(path);
          console.log(`🤖 Response status: ${response.status}, Content-Type: ${response.headers.get('content-type')}`);
          
          if (response.ok) {
            const text = await response.text();
            console.log(`🤖 Response first 100 chars: ${text.substring(0, 100)}`);
            
            // Check if it's actually JSON
            if (text.trim().startsWith('{')) {
              data = JSON.parse(text);
              console.log(`🤖 ✅ Successfully loaded from: ${path}`);
              break;
            } else {
              console.log(`🤖 ❌ Not JSON content at ${path}`);
            }
          }
        } catch (pathError) {
          console.log(`🤖 ❌ Failed to load from ${path}:`, pathError);
        }
      }
      
      if (!data) {
        console.error('🤖 ❌ Could not load chatbots.json from any path');
        console.log('🤖 Falling back to hardcoded common bots...');
        
        // Fallback to common bots if file can't be loaded
        const commonBots = [
          'nightbot', 'streamelements', 'moobot', 'streamlabs', 'commanderroot',
          'wizebot', 'fossabot', 'stay_hydrated_bot', 'soundalerts', 'anotherttvviewer',
          'lurxx', 'amazeful', 'thelurkertv', 'r3ddybot', 'r3ddybot_dev'
        ];
        
        commonBots.forEach(botName => {
          this.knownBots.add(botName.toLowerCase());
        });
        
        console.log(`🤖 Loaded ${this.knownBots.size} fallback bots`);
        this.botsLoaded = true;
        return;
      }

      console.log('🤖 Raw bot data loaded, checking format...');
      console.log('🤖 Data type:', typeof data);
      console.log('🤖 Data keys:', Object.keys(data));
      console.log('🤖 Has bots property:', 'bots' in data);
      console.log('🤖 Bots is array:', Array.isArray(data.bots));
      console.log('🤖 Bots length:', data.bots?.length);
      
      if (Array.isArray(data.bots)) {
        console.log('🤖 Processing bot array...');
        let processedCount = 0;
        let validCount = 0;
        
        data.bots.forEach((bot: any, index: number) => {
          processedCount++;
          if (index < 5) { // Only log first 5 to avoid spam
            console.log(`🤖 Processing bot ${index}: `, bot, 'Type:', typeof bot, 'Is Array:', Array.isArray(bot));
          }
          
          if (Array.isArray(bot) && typeof bot[0] === 'string') {
            validCount++;
            const botName = bot[0].toLowerCase();
            this.knownBots.add(botName);
            // Log first 10 bots loaded for debugging
            if (index < 10) {
              console.log(`🤖 ✅ Loaded bot #${index + 1}: "${botName}"`);
            }
          } else if (index < 5) {
            console.log(`🤖 ❌ Invalid bot format at index ${index}:`, bot);
          }
        });
        
        console.log(`🤖 Processed ${processedCount} entries, ${validCount} valid bots`);
        console.log(`🤖 Successfully loaded ${this.knownBots.size} known bots for detection`);
        
        // Show a sample of loaded bot names
        const sampleBots = Array.from(this.knownBots).slice(0, 5);
        console.log(`🤖 Sample bot names: ${sampleBots.join(', ')}`);
        
      } else {
        console.warn('❌ Invalid format in chatbots.json. Expected "bots" to be an array.');
        console.log('❌ Actual bots value:', data.bots);
      }
      
      this.botsLoaded = true;
    } catch (error) {
      console.error('❌ Error loading known bots:', error);
      this.botsLoaded = true; // Mark as loaded even on error to prevent retries
    }
  }

  private async ensureBotsLoaded(): Promise<void> {
    if (!this.botsLoaded && this.botsLoadingPromise) {
      await this.botsLoadingPromise;
    }
  }

  private async fetchWithAuth(endpoint: string): Promise<any> {
    const response = await fetch(`https://api.twitch.tv/helix${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Client-Id': this.clientId,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private async postWithAuth(endpoint: string, data: any = {}): Promise<any> {
    const response = await fetch(`https://api.twitch.tv/helix${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Client-Id': this.clientId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.status === 204 ? null : response.json();
  }

  async shoutoutUser(broadcasterId: string, toUserId: string): Promise<void> {
    await this.postWithAuth(`/chat/shoutouts`, {
      from_broadcaster_id: broadcasterId,
      to_broadcaster_id: toUserId,
      moderator_id: broadcasterId,
    });
  }

  async startRaid(fromBroadcasterId: string, toBroadcasterId: string): Promise<void> {
    await this.postWithAuth(`/raids`, {
      from_broadcaster_id: fromBroadcasterId,
      to_broadcaster_id: toBroadcasterId,
    });
  }

  async banUser(_broadcasterId: string, userId: string): Promise<void> {
    await this.postWithAuth(`/moderation/bans`, {
      data: {
        user_id: userId,
        reason: 'Banned through FirePanel',
      },
    });
  }

  async getChannelViewers(channelId: string): Promise<Viewer[]> {
    try {
      // Ensure bots are loaded before processing viewers
      await this.ensureBotsLoaded();
      console.log(`🤖 Bot loading status: ${this.botsLoaded}, Known bots count: ${this.knownBots.size}`);

      console.log(`👥 Fetching viewers for channel ${channelId}...`);
      const chattersResponse = await this.fetchWithAuth(`/chat/chatters?broadcaster_id=${channelId}&moderator_id=${channelId}`);
      const chatters = chattersResponse.data;
      console.log(`👥 Found ${chatters.length} chatters`);

      if (!chatters || chatters.length === 0) {
        console.log('👥 No chatters found');
        return [];
      }

      const userIds = chatters.map((chatter: any) => chatter.user_id);
      const usersResponse = await this.fetchWithAuth(`/users?id=${userIds.join('&id=')}`);
      const users = usersResponse.data;

      const modsResponse = await this.fetchWithAuth(`/moderation/moderators?broadcaster_id=${channelId}`);
      const mods = new Set(modsResponse.data.map((mod: any) => mod.user_id));

      const vipsResponse = await this.fetchWithAuth(`/channels/vips?broadcaster_id=${channelId}`);
      const vips = new Set(vipsResponse.data.map((vip: any) => vip.user_id));

      console.log(`👥 Processing ${users.length} users for bot detection...`);
      
      const viewers = users.map((user: any) => {
        const chatter = chatters.find((c: any) => c.user_id === user.id);
        let type: Viewer['type'] = 'viewer';
        let isBot = false;

        if (user.id === channelId) {
          type = 'broadcaster';
        } else if (mods.has(user.id)) {
          type = 'moderator';
        } else if (vips.has(user.id)) {
          type = 'vip';
        }

        // Check for bot with detailed logging
        const username = user.login;
        console.log(`🔍 Checking user: "${username}" (${username.toLowerCase()})`);
        isBot = this.isBot(username);

        return {
          username: user.login,
          displayName: user.display_name,
          id: user.id,
          type,
          profileImageUrl: user.profile_image_url,
          isBot,
          joinedAt: new Date(chatter.joined_at),
          badges: [],
        };
      });

      const detectedBots = viewers.filter((v: Viewer) => v.isBot);
      const botCount = detectedBots.length;
      console.log(`🤖 FINAL RESULT: Processed ${viewers.length} viewers, identified ${botCount} bots`);
      console.log(`🤖 Bots detected: ${detectedBots.map((v: Viewer) => v.username).join(', ') || 'none'}`);

      return viewers;
    } catch (error) {
      console.error('❌ Error fetching viewers:', error);
      return [];
    }
  }

  isBot(username: string): boolean {
    const normalizedUsername = username.toLowerCase();
    const result = this.knownBots.has(normalizedUsername);
    
    if (result) {
      console.log(`✅ BOT DETECTED: "${username}" (normalized: "${normalizedUsername}")`);
    } else {
      // Only log non-bots for first few to avoid spam
      if (Math.random() < 0.1) { // 10% chance to log non-bots to avoid spam
        console.log(`❌ Not a bot: "${username}" (normalized: "${normalizedUsername}")`);
      }
    }
    
    return result;
  }

  getViewerCounts(viewers: Viewer[]): { total: number; real: number; bots: number } {
    console.log(`📊 Calculating viewer counts for ${viewers.length} viewers...`);
    
    const detectedBots = viewers.filter((v: Viewer) => v.isBot);
    const bots = detectedBots.length;
    
    console.log(`📊 Bot breakdown:`);
    detectedBots.forEach((bot, index) => {
      console.log(`  Bot ${index + 1}: ${bot.username} (${bot.type})`);
    });
    
    const counts = {
      total: viewers.length,
      real: viewers.length - bots,
      bots,
    };

    console.log(`📊 FINAL VIEWER COUNTS: Total: ${counts.total}, Real: ${counts.real}, Bots: ${counts.bots}`);
    return counts;
  }

  // Debug method to test bot detection - call from browser console
  async debugBotDetection(): Promise<void> {
    console.log('🔧 DEBUG: Testing bot detection system...');
    
    await this.ensureBotsLoaded();
    
    console.log(`🔧 Bot loading status: ${this.botsLoaded}`);
    console.log(`🔧 Known bots count: ${this.knownBots.size}`);
    
    if (this.knownBots.size > 0) {
      // Test with known bot names from the JSON
      const testBots = ['nightbot', 'streamelements', 'moobot', 'streamlabs', 'commanderroot'];
      
      console.log('🔧 Testing known bot names:');
      testBots.forEach(botName => {
        const result = this.isBot(botName);
        console.log(`  ${botName}: ${result ? '✅ DETECTED' : '❌ NOT DETECTED'}`);
      });
      
      // Show sample of loaded bots
      const sampleBots = Array.from(this.knownBots).slice(0, 10);
      console.log(`🔧 First 10 loaded bot names: ${sampleBots.join(', ')}`);
    } else {
      console.log('🔧 ❌ No bots were loaded! Check the chatbots.json file.');
    }
  }
}
